import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "../../../../context/ThemeContext";
import {
  getCardBackground,
  getLightBorder,
  getPrimaryText
} from "../../../../utils/themeUtils";

interface InvoiceActionsProps {
  onEdit: () => void;
  onDelete: () => void;
  onAccept: () => void;
  onRefund?: () => void;
  isPaid: boolean;
  invoice?: any;
  openMenuId?: string | null;
  setOpenMenuId?: (id: string | null) => void;
}

const InvoiceActions: React.FC<InvoiceActionsProps> = ({
  onEdit,
  onDelete,
  onAccept,
  onRefund,
  isPaid,
  invoice,
  openMenuId: externalOpenMenuId,
  setOpenMenuId: externalSetOpenMenuId,
}) => {
  const { isDarkMode } = useTheme();
  // Use local state if external menu control is not provided
  const [localOpenMenuId, setLocalOpenMenuId] = useState<string | null>(null);
  const openMenuId = externalOpenMenuId !== undefined ? externalOpenMenuId : localOpenMenuId;
  const setOpenMenuId = externalSetOpenMenuId || setLocalOpenMenuId;
  const invoiceId = invoice?.id || 'default';
  const isOpen = openMenuId === invoiceId;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right + window.scrollX - 160 // Position to the left of the button
      });
    }
  }, [isOpen]);

  return (
    <div className="relative inline-block text-left">
      <button
        aria-label="Invoice actions"
        ref={buttonRef}
        onClick={() => setOpenMenuId(isOpen ? null : invoiceId)}
        className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          className="lucide lucide-ellipsis-vertical-icon lucide-ellipsis-vertical"
        >
          <circle cx="12" cy="12" r="1" />
          <circle cx="12" cy="5" r="1" />
          <circle cx="12" cy="19" r="1" />
        </svg>
      </button>

      {isOpen && (
        <div
          className={`fixed z-50 w-40 ${getCardBackground(isDarkMode)} ${getLightBorder(isDarkMode)} border rounded shadow-lg`}
          style={{
            top: `${popoverPosition.top}px`,
            left: `${popoverPosition.left}px`
          }}
        >
          <button
            onClick={() => {
              onEdit();
              setOpenMenuId(isOpen ? null : invoiceId);
            }}
            className={`w-full text-left px-4 py-2 ${getPrimaryText(isDarkMode)} ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            Edit
          </button>
          <button
            onClick={() => {
              onDelete();
              setOpenMenuId(isOpen ? null : invoiceId);
            }}
            className={`w-full text-left px-4 py-2 text-red-600 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            Delete
          </button>

          {!isPaid && (
            <button
              onClick={() => {
                onAccept();
                setOpenMenuId(isOpen ? null : invoiceId);
              }}
              className={`w-full text-left px-4 py-2 text-green-600 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            >
              Accept Payment
            </button>
          )}

          {isPaid && onRefund && (
            <button
              onClick={() => {
                onRefund();
                setOpenMenuId(isOpen ? null : invoiceId);
              }}
              className={`w-full text-left px-4 py-2 text-orange-600 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            >
              Issue Refund
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default InvoiceActions;
